/**
 * useAdminPromos Hook
 *
 * CRUD over the promoCodes collection (doc id = uppercase code).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts';

export interface PromoCode {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  maxRedemptions: number | null;
  redemptionCount: number;
  minNights: number | null;
  minSubtotal: number | null;
  expiresAt: Date | null;
  createdAt: Date;
}

export interface NewPromoCode {
  code: string;
  type: 'percent' | 'fixed';
  value: number;
  active: boolean;
  maxRedemptions?: number | null;
  minNights?: number | null;
  minSubtotal?: number | null;
  expiresAt?: string; // yyyy-mm-dd
}

const toDate = (val: unknown): Date | null =>
  val instanceof Timestamp ? val.toDate() : null;

export const useAdminPromos = () => {
  const { user } = useAuth();
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const snap = await getDocs(collection(db, 'promoCodes'));
        if (cancelled) return;
        const rows = snap.docs.map((d) => {
          const data = d.data();
          return {
            code: d.id,
            type: (data.type as 'percent' | 'fixed') || 'percent',
            value: data.value || 0,
            active: data.active === true,
            maxRedemptions: data.maxRedemptions ?? null,
            redemptionCount: data.redemptionCount || 0,
            minNights: data.minNights ?? null,
            minSubtotal: data.minSubtotal ?? null,
            expiresAt: toDate(data.expiresAt),
            createdAt: toDate(data.createdAt) || new Date(),
          } as PromoCode;
        });
        rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setPromos(rows);
      } catch (err) {
        console.error('Failed to fetch promo codes:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchKey]);

  const create = useCallback(
    async (data: NewPromoCode) => {
      const code = data.code.trim().toUpperCase();
      if (!code) throw new Error('Code required');
      const ref = doc(db, 'promoCodes', code);
      const existing = await getDoc(ref);
      if (existing.exists()) throw new Error('That code already exists');

      await setDoc(ref, {
        type: data.type,
        value: data.value,
        active: data.active,
        maxRedemptions: data.maxRedemptions ?? null,
        redemptionCount: 0,
        minNights: data.minNights ?? null,
        minSubtotal: data.minSubtotal ?? null,
        expiresAt: data.expiresAt ? Timestamp.fromDate(new Date(data.expiresAt)) : null,
        createdBy: user?.uid || '',
        createdAt: serverTimestamp(),
      });
      refetch();
    },
    [user, refetch]
  );

  const toggleActive = useCallback(
    async (code: string, active: boolean) => {
      await updateDoc(doc(db, 'promoCodes', code), { active });
      refetch();
    },
    [refetch]
  );

  const remove = useCallback(
    async (code: string) => {
      await deleteDoc(doc(db, 'promoCodes', code));
      refetch();
    },
    [refetch]
  );

  return { promos, isLoading, create, toggleActive, remove, refetch };
};

export default useAdminPromos;
